import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { songsApi } from '@/services/api/songs';
import { commentsApi } from '@/services/api/comments';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CoverArt from '@/components/common/CoverArt';
import { formatDate, formatTimeAgo } from '@/utils/formatters';
import { ArrowLeft, MessageSquare } from 'lucide-react';

export default function SongDetailsPage() {
  const { songId } = useParams<{ songId: string }>();
  const [commentText, setCommentText] = useState('');
  const { playSong } = useAudioPlayer();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['songDetails', songId],
    queryFn: () => songsApi.getSongDetails(Number(songId)),
    enabled: !!songId,
  });

  const commentMutation = useMutation({
    mutationFn: (text: string) =>
      commentsApi.createComment(Number(songId), { commentText: text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songDetails', songId] });
      setCommentText('');
    },
  });

  async function handleSubmitComment(e: React.FormEvent) {
    e.preventDefault();
    if (!commentText.trim()) return;
    commentMutation.mutate(commentText);
  }

  if (isLoading) return <LoadingSpinner />;
  if (!data || !data.song) return <div className="text-center text-white p-8">Song not found</div>;

  const { song, revisions = [], comments = [] } = data;

  // Filter comments to only show those for the main song (not revisions)
  // Sort by done status (not done first) then by date (newest first)
  const songComments = comments
    .filter(comment => !comment.songRevisionId)
    .sort((a, b) => {
      // Sort by done status first (false/undefined before true)
      if (a.done !== b.done) {
        return (a.done ? 1 : 0) - (b.done ? 1 : 0);
      }
      // Then by date (newest first)
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    });

  // Create a combined list with original song and revisions
  const allVersions = [
    // Original song as first item
    {
      songRevisionId: null,
      songId: song.songId,
      songName: `${song.songName} (Original)`,
      audioFileUrl: song.audioFileUrl,
      sessionFileUrl: song.sessionFileUrl,
      coverArtUrl: song.coverArtUrl,
      isFavorite: false,
      pinned: false,
      revised: song.created,
    },
    // Then all revisions
    ...revisions
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-white">
            <ArrowLeft size={20} />
            Back to Songs
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              {/* Row 1: cover art + title */}
              <div className="flex items-center gap-6">
                <CoverArt
                  songId={song.songId}
                  coverArtUrl={song.coverArtUrl}
                  alt={song.songName}
                  className="w-32 h-32 object-cover rounded"
                />
                <h1 className="flex-1 text-3xl font-bold text-white">{song.songName}</h1>
              </div>

              {/* Row 2: created date */}
              <p className="text-text-secondary mt-4">
                Created: {formatDate(song.created)}
              </p>

              {/* Row 3: comment label */}
              {songComments.length > 0 && (
                <p className="text-text-muted text-sm flex items-center gap-1 mt-2">
                  <MessageSquare size={14} />
                  {songComments.length} {songComments.length === 1 ? 'comment' : 'comments'}
                </p>
              )}
            </div>

            {/* Revisions */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Versions</h2>
              <div className="space-y-2">
                {allVersions.map((version) => {
                  const versionCommentCount = comments.filter(
                    c => version.songRevisionId
                      ? c.songRevisionId === version.songRevisionId
                      : !c.songRevisionId
                  ).length;

                  const isOriginal = version.songRevisionId === null;

                  return (
                    <div key={version.songRevisionId || 'original'} className="card">
                      <div className="flex items-center justify-between">
                        {isOriginal ? (
                          <div className="flex-1">
                            <p className="text-white font-medium">
                              {version.songName}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-text-secondary text-sm">
                                {formatDate(version.revised)}
                              </p>
                              {versionCommentCount > 0 && (
                                <p className="text-text-muted text-xs flex items-center gap-1">
                                  <MessageSquare size={12} />
                                  {versionCommentCount}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Link
                            to={`/songs/${song.songId}/revisions/${version.songRevisionId}`}
                            className="flex-1"
                          >
                            <p className="text-white font-medium hover:text-primary transition-colors">
                              {version.songName}
                              {version.pinned && (
                                <span className="ml-2 text-xs text-text-muted italic">📍 Pinned</span>
                              )}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-text-secondary text-sm">
                                {formatDate(version.revised)}
                              </p>
                              {versionCommentCount > 0 && (
                                <p className="text-text-muted text-xs flex items-center gap-1">
                                  <MessageSquare size={12} />
                                  {versionCommentCount}
                                </p>
                              )}
                            </div>
                          </Link>
                        )}
                        <button
                          onClick={() => isOriginal ? playSong(song) : playSong(song, version)}
                          className="btn-secondary ml-4"
                        >
                          Play
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Comments Sidebar */}
          <div>
            <div className="sticky top-4">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <MessageSquare size={20} />
                Comments ({songComments.length})
              </h2>

              {/* Add Comment Form */}
              <form onSubmit={handleSubmitComment} className="mb-6">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  className="input resize-none h-24 mb-2"
                />
                <button
                  type="submit"
                  disabled={!commentText.trim() || commentMutation.isPending}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {commentMutation.isPending ? 'Posting...' : 'Post Comment'}
                </button>
              </form>

              {/* Comments List */}
              <div className="space-y-4">
                {songComments.length > 0 ? (
                  songComments.map((comment) => (
                    <div
                      key={comment.commentId}
                      className={`card ${comment.done ? 'opacity-50' : ''}`}
                    >
                      <p className={`text-white text-sm mb-2 ${comment.done ? 'line-through text-text-muted' : ''}`}>
                        {comment.commentText}
                      </p>
                      <p className="text-text-muted text-xs">
                        {formatTimeAgo(comment.created)}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-text-secondary text-sm">No notes yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
